// ─────────────────────────────────────────────────────────────
// iOS Safari (browser mode, not PWA) has NO Notification API.
// iOS 16.4+ supports it ONLY in standalone/PWA mode.
// Every access to Notification, navigator.serviceWorker, and
// messaging must be guarded or the app crashes with a blank screen.
// ─────────────────────────────────────────────────────────────

import { useEffect, useCallback, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../api/apiServices";

// ── Safe capability checks ────────────────────────────────────
const supportsNotifications = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  typeof Notification !== "undefined";

const supportsServiceWorker = () =>
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator;

const supportsMessaging = () =>
  supportsNotifications() && supportsServiceWorker();

// ── Register FCM token with backend ──────────────────────────
async function saveFCMToken(token) {
  try {
    await apiClient.post("/mtrade/notifications/register-token", { token });
  } catch (err) {
    console.warn("[FCM] Failed to save token:", err.message);
  }
}

function getNotifIcon(type) {
  const map = {
    deposit:      "💰",
    buy:          "📈",
    sell:         "💸",
    bill_payment: "✅",
    cashback:     "🎁",
    streak:       "🔥",
    referral:     "👥",
    renewal:      "📺",
  };
  return map[type] || "🔔";
}

// ─────────────────────────────────────────────────────────────
export function useNotifications(isAuthenticated) {
  // ✅ CRITICAL: Never access Notification.permission synchronously.
  // On iOS Safari (browser mode) Notification is undefined → instant crash.
  const [permission, setPermission] = useState(() => {
    if (!supportsNotifications()) return "unsupported";
    return Notification.permission;
  });
  const [fcmToken, setFcmToken] = useState(null);

  const requestPermission = useCallback(async () => {
    // Guard: iOS browser mode, very old Safari, or Firefox private mode
    if (!supportsMessaging()) {
      console.warn("[FCM] Push notifications not supported on this browser/platform");
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== "granted") {
        console.log("[FCM] Notification permission denied");
        return;
      }

      // ✅ Lazy-import Firebase messaging only when actually needed.
      // Importing it at module level causes issues on iOS Safari because
      // Firebase checks for indexedDB and ServiceWorker support at import time.
      const { messaging, getToken, VAPID_KEY } = await import("../services/firebaseConfig");

      // Register service worker
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" }
      );
      console.log("[SW] Service worker registered");

      const token = await getToken(messaging, {
        vapidKey:                   VAPID_KEY,
        serviceWorkerRegistration:  registration,
      });

      if (token) {
        setFcmToken(token);
        await saveFCMToken(token);
        console.log("[FCM] Token registered");
      }
    } catch (err) {
      // Silent fail — never let notification errors break the app
      console.warn("[FCM] Permission/token error:", err.message);
    }
  }, []);

  // ── Auto-request when authenticated ───────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!supportsMessaging()) return; // iOS browser / unsupported — skip entirely

    if (Notification.permission === "granted") {
      requestPermission();
      return;
    }
    if (Notification.permission === "default") {
      const timer = setTimeout(requestPermission, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, requestPermission]);

  // ── Handle foreground messages ────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!supportsMessaging()) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

    let unsubscribe = () => {};

    // Lazy import so iOS never executes Firebase messaging code
    import("../services/firebaseConfig")
      .then(({ messaging, onMessage }) => {
        unsubscribe = onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          const data            = payload.data || {};
          const icon            = getNotifIcon(data.type);
          const click_action    = data.click_action || "/";

          toast(
            <div
              className="flex items-start gap-3"
              onClick={() => { window.location.href = click_action; }}>
              <span className="text-2xl flex-shrink-0">{icon}</span>
              <div>
                <p className="font-bold text-slate-800 text-sm">{title}</p>
                <p className="text-slate-500 text-xs mt-0.5">{body}</p>
              </div>
            </div>,
            { position: "top-center", autoClose: 5000, style: { cursor: "pointer" } }
          );
        });
      })
      .catch(err => console.warn("[FCM] Messaging import failed:", err.message));

    return () => unsubscribe();
  }, [isAuthenticated]);

  return { permission, fcmToken, requestPermission };
}
